import React, { useEffect, useState } from 'react';
import { firestore } from '../config/firebaseConfig';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { FaFilter, FaCalendarAlt } from 'react-icons/fa';

const FeedAtividades = () => {
    const [atividades, setAtividades] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dataFiltro, setDataFiltro] = useState(
        new Date().toISOString().split('T')[0]
    );
    const [filtroTipo, setFiltroTipo] = useState('todos');
    const [contatosMap, setContatosMap] = useState({});
    const [sortConfig, setSortConfig] = useState({
        key: 'horario',
        direction: 'desc'
    });

    const tenantId =
        localStorage.getItem('tenantId') || 'unknownTenant';

    // ================================
    // Helpers
    // ================================

    const isSameDay = (date1, date2) => {
        return (
            date1 &&
            date2 &&
            date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate()
        );
    };

    const formatarTempo = (tempoEmMs) => {
        if (!tempoEmMs && tempoEmMs !== 0) return '-';

        const segundos = Math.floor((tempoEmMs / 1000) % 60);
        const minutos = Math.floor((tempoEmMs / (1000 * 60)) % 60);
        const horas = Math.floor((tempoEmMs / (1000 * 60 * 60)) % 24);

        let resultado = '';
        if (horas > 0) resultado += `${horas}h `;
        if (minutos > 0 || horas > 0) resultado += `${minutos}m `;
        resultado += `${segundos}s`;

        return resultado;
    };

    // ================================
    // Carregar contatos
    // ================================

    useEffect(() => {
        const unsubscribe = onSnapshot(
            query(
                collection(firestore, 'contatos'),
                where('tenantId', '==', tenantId)
            ),
            (snapshot) => {
                const contatos = {};
                snapshot.forEach((doc) => {
                    const contatoData = doc.data();
                    contatos[contatoData.telefone] = {
                        nome: contatoData.nome || '-',
                        tipoContato: contatoData.tipoContato || '-',
                        origem: contatoData.origem || '-',
                        status: contatoData.status || '-'
                    };
                });
                setContatosMap(contatos);
            }
        );

        return () => unsubscribe();
    }, [tenantId]);

    // ================================
    // Carregar atividades
    // ================================

    useEffect(() => {
        const dataFiltroDate = new Date(dataFiltro + 'T00:00:00');

        setLoading(true);
        setAtividades([]);

        // -------- CONTATOS --------
        const unsubscribeContatos = onSnapshot(
            query(
                collection(firestore, 'contatos'),
                where('tenantId', '==', tenantId)
            ),
            (snapshot) => {
                const contatosArray = [];

                snapshot.forEach((doc) => {
                    const contato = doc.data();

                    const dataCriacaoDate =
                        contato.dataCriacao?.toDate?.();

                    if (
                        dataCriacaoDate &&
                        (filtroTipo === 'contatos' ||
                            filtroTipo === 'todos') &&
                        isSameDay(dataCriacaoDate, dataFiltroDate)
                    ) {
                        contatosArray.push({
                            ...contato,
                            tipo: 'contato',
                            id: doc.id,
                            horario: dataCriacaoDate
                        });
                    }
                });

                setAtividades((prev) => {
                    const filtered = prev.filter(
                        (a) => a.tipo !== 'contato'
                    );
                    return [...filtered, ...contatosArray].sort(
                        (a, b) =>
                            b.horario.getTime() -
                            a.horario.getTime()
                    );
                });

                setLoading(false);
            }
        );

        // -------- RESPOSTAS --------
        const unsubscribeRespostas = onSnapshot(
            query(
                collection(firestore, 'respostas'),
                where('tenantId', '==', tenantId)
            ),
            (snapshot) => {
                const respostasArray = [];

                snapshot.forEach((doc) => {
                    const resposta = doc.data();

                    const timestampDate =
                        resposta.timestamp?.toDate?.();

                    const contatoInfo =
                        contatosMap[resposta.telefone] || {
                            nome: '-',
                            tipoContato: '-',
                            origem: '-',
                            status: '-'
                        };

                    if (
                        timestampDate &&
                        (filtroTipo === 'respostas' ||
                            filtroTipo === 'todos') &&
                        isSameDay(timestampDate, dataFiltroDate)
                    ) {
                        respostasArray.push({
                            ...resposta,
                            tipo: 'resposta',
                            id: doc.id,
                            horario: timestampDate,
                            isCorrect: resposta.acertou
                                ? '✅'
                                : '❌',
                            responseTime: formatarTempo(
                                resposta.responseTime
                            ),
                            nomeContato: contatoInfo.nome,
                            tipoContato: contatoInfo.tipoContato,
                            origem: contatoInfo.origem,
                            status: contatoInfo.status,
                            questionId: resposta.idquestao
                        });
                    }
                });

                setAtividades((prev) => {
                    const filtered = prev.filter(
                        (a) => a.tipo !== 'resposta'
                    );
                    return [...filtered, ...respostasArray].sort(
                        (a, b) =>
                            b.horario.getTime() -
                            a.horario.getTime()
                    );
                });

                setLoading(false);
            }
        );

        return () => {
            unsubscribeContatos();
            unsubscribeRespostas();
        };
    }, [dataFiltro, filtroTipo, contatosMap, tenantId]);

    // ================================
    // Sort
    // ================================

    const requestSort = (key) => {
        let direction = 'asc';
        if (
            sortConfig.key === key &&
            sortConfig.direction === 'asc'
        ) {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedAtividades = [...atividades].sort((a, b) => {
        const direction =
            sortConfig.direction === 'asc' ? 1 : -1;

        if (sortConfig.key === 'horario') {
            return (
                (a.horario?.getTime() -
                    b.horario?.getTime()) * direction
            );
        }

        if (a[sortConfig.key] < b[sortConfig.key])
            return -1 * direction;
        if (a[sortConfig.key] > b[sortConfig.key])
            return 1 * direction;

        return 0;
    });

    const contadorAtividades = atividades.length;

    // ================================
    // Render
    // ================================

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loader"></div>
                <p>Buscando suas atividades...</p>
            </div>
        );
    }

    return (
        <div className="feed-atividades-container">
            <h3 className="feed-title">
                Feed de Atividades do Dia
            </h3>

            {filtroTipo !== 'todos' && (
                <div className="contador-atividades">
                    <p>Total: {contadorAtividades}</p>
                </div>
            )}

            <div className="feed-atividades-filter">
                <label>
                    <FaCalendarAlt /> Escolha a data:
                </label>
                <input
                    type="date"
                    value={dataFiltro}
                    onChange={(e) =>
                        setDataFiltro(e.target.value)
                    }
                />
            </div>

            <div className="feed-atividades-filter">
                <label>
                    <FaFilter /> Filtrar por:
                </label>
                <select
                    value={filtroTipo}
                    onChange={(e) =>
                        setFiltroTipo(e.target.value)
                    }
                >
                    <option value="todos">Todos</option>
                    <option value="contatos">Contatos</option>
                    <option value="respostas">Respostas</option>
                </select>
            </div>

            {sortedAtividades.length === 0 ? (
                <div className="empty-state">
                    <p>
                        Nenhuma interação encontrada
                        para a data selecionada.
                    </p>
                </div>
            ) : (
                <table className="feed-atividades-table">
                    <thead>
                        <tr>
                            <th onClick={() => requestSort('horario')}>
                                Horário
                            </th>
                            <th>Nome</th>
                            <th>Evento</th>
                            <th>Tipo Contato</th>
                            <th>Origem</th>
                            <th>Status</th>
                            <th>Questão</th>
                            <th>Resultado</th>
                            <th>Tempo</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedAtividades.map((atividade) => (
                            <tr key={atividade.id}>
                                <td>
                                    {atividade.horario?.toLocaleTimeString(
                                        'pt-BR',
                                        {
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        }
                                    )}
                                </td>
                                <td>
                                    {atividade.tipo === 'contato'
                                        ? atividade.nome
                                        : atividade.nomeContato}
                                </td>
                                <td>
                                    {atividade.tipo === 'contato'
                                        ? 'Contato'
                                        : 'Resposta'}
                                </td>
                                <td>{atividade.tipoContato}</td>
                                <td>{atividade.origem}</td>
                                <td>{atividade.status}</td>
                                <td>
                                    {atividade.questionId || '-'}
                                </td>
                                <td>
                                    {atividade.isCorrect || '-'}
                                </td>
                                <td>
                                    {atividade.responseTime || '-'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default FeedAtividades;
